/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, TextField, Typography, Switch, FormControlLabel, InputAdornment } from "@mui/material";
import React from "react";

import Topbar from "../components/topbar";
import { useToast } from "../providers/toaster-provider";
import { useUser } from "../providers/user-provider";
import { useSettings } from "../providers/settings-provider";
import DeletePopup from "../components/popup/delete";
import SubscriptionPlans from "../components/popup/subscription-plans";
import Cookies from "js-cookie";
import SectionWrapper from "../components/section";

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';

const DevOptions: React.FC = () => {
    const { user } = useUser();
    const { showSuccess, showError } = useToast();
    const { formatDate } = useSettings();

    // Form fields
    const [tolerancePercent, setTolerancePercent] = React.useState<number>(0);
    const [showDebug, setShowDebug] = React.useState<boolean>(() => {
        const savedDebugState = localStorage.getItem('showDebug');
        return savedDebugState !== null ? JSON.parse(savedDebugState) : false;
    });
    // Gates visibility of the "Cash Position" toggle on the transactions
    // page - a perf-diagnostic switch (skips the client-side balance/cash-
    // flow calculation entirely) that only appears there when this is on,
    // same hidden-behind-secret-options pattern as showDebug above.
    const [showCashPositionToggle, setShowCashPositionToggle] = React.useState<boolean>(() => {
        const saved = localStorage.getItem('showCashPositionToggle');
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [entriesToCompare, setEntriesToCompare] = React.useState<number>(0);
    const [descriptionCharacterToCompare, setDescriptionCharacterToCompare] = React.useState<number>(0);
    const [showDeletePopup, setShowDeletePopup] = React.useState<boolean>(false);
    const [showSubscriptionPlans, setShowSubscriptionPlans] = React.useState<boolean>(false);
    const [rangeSetup, setRangeSetup] = React.useState<{
        daily: [number, number];
        weekly: [number, number];
        fortnightly: [number, number];
        monthly: [number, number];
        quarterly: [number, number];
        yearly: [number, number];
    }>({
        daily: [0, 0],
        weekly: [0, 0],
        fortnightly: [0, 0],
        monthly: [0, 0],
        quarterly: [0, 0],
        yearly: [0, 0],
    });

    // Provision Reconciliation scoring configuration
    const [provisionMatchConfig, setProvisionMatchConfig] = React.useState<{
        amountPoints: number;
        amountTolerance: number;
        sameDayPoints: number;
        within1DayPoints: number;
        within3DayPoints: number;
        within7DayPoints: number;
        within30DayPoints: number;
        descriptionTokenThresholds: Array<{ minTokens: number; score: number }>;
        categoryBasePoints: number;
        categoryBonusPoints: number;
        minimumThreshold: number;
    }>({
        amountPoints: 1.25,
        amountTolerance: 0.01,
        sameDayPoints: 1.0,
        within1DayPoints: 0.9,
        within3DayPoints: 0.7,
        within7DayPoints: 0.4,
        within30DayPoints: 0.15,
        descriptionTokenThresholds: [
            { minTokens: 1, score: 0.25 },
            { minTokens: 2, score: 0.5 },
            { minTokens: 3, score: 0.75 },
            { minTokens: 4, score: 1.0 },
        ],
        categoryBasePoints: 0.25,
        categoryBonusPoints: 0.75,
        minimumThreshold: 2.0,
    });

    // Provision Occurrence Cutoff Configuration
    const [provisionCutoffConfig, setProvisionCutoffConfig] = React.useState<{
        weekly: number;
        fortnightly: number;
        monthly: number;
        quarterly: number;
        yearly: number;
    }>({
        weekly: 16,
        fortnightly: 30,
        monthly: 70,
        quarterly: 200,
        yearly: 760,
    });

    // Original values for change detection
    const [originalValues, setOriginalValues] = React.useState<{
        tolerancePercent: number;
        entriesToCompare: number;
        descriptionCharacterToCompare: number;
        rangeSetup: typeof rangeSetup;
        provisionMatchConfig: typeof provisionMatchConfig;
    } | null>(null);

    const [fetchCompany, setFetchCompany] = useApi();
    const [updateCompany, setUpdateCompany] = useApi();
    const [deleteCompany, setDeleteCompany] = useApi();
    const [autoProvision, setAutoProvision] = useApi();
    const [fetchXeroIntegration, setFetchXeroIntegration] = useApi();
    const [staleImport, setStaleImport] = useApi();

    const [latestAutoProvision, setLatestAutoProvision] = React.useState<{
        status: string;
        elapsedTime: string;
        createdAt: string;
        transactionsScanned: number;
        provisionsCreated: number;
        config: {
            startDate: string;
            direction: string;
            numberOfDays: number;
            concurrentLimit: number;
            timeLimit: number;
        };
    } | null>(null);

    // Track elapsed time increment for continuous timer
    const [elapsedIncrement, setElapsedIncrement] = React.useState<number>(0);

    // Auto-Provisioning parameters
    const [autoProvisionParams, setAutoProvisionParams] = React.useState<{
        startDate: string;
        direction: 'asc' | 'desc';
        numberOfDays: number;
        concurrentLimit: number;
        timeLimit: number; // minutes
    }>({
        startDate: new Date().toISOString().split('T')[0],
        direction: 'desc',
        numberOfDays: 90,
        concurrentLimit: 5,
        timeLimit: 360,
    });

    // Basiq parameters
    const [basiqEmail, setBasiqEmail] = React.useState<string>('');
    const [basiqUserId, setBasiqUserId] = React.useState<string>('');
    const [basiqTransactionDateRange, setBasiqTransactionDateRange] = React.useState<{
        from: string;
        to: string;
    }>({
        from: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0],
    });

    const [pullTransactions, setPullTransactions] = useApi();
    const [createBasiqUser, setCreateBasiqUser] = useApi();
    const [connectBasiqUser, setConnectBasiqUser] = useApi();

    const [autoReconcile, setAutoReconcile] = useApi();

    // Save debug toggle to localStorage whenever it changes
    React.useEffect(() => {
        localStorage.setItem('showDebug', JSON.stringify(showDebug));
    }, [showDebug]);

    React.useEffect(() => {
        localStorage.setItem('showCashPositionToggle', JSON.stringify(showCashPositionToggle));
    }, [showCashPositionToggle]);

    React.useEffect(() => {
        document.title = "FineAnts | Dev Options";
    }, []);

    // Load debug toggle from localStorage
    React.useEffect(() => {
        const savedDebugState = localStorage.getItem('showDebug');
        if (savedDebugState !== null) {
            setShowDebug(JSON.parse(savedDebugState));
        }
        const savedCashPositionToggleState = localStorage.getItem('showCashPositionToggle');
        if (savedCashPositionToggleState !== null) {
            setShowCashPositionToggle(JSON.parse(savedCashPositionToggleState));
        }
    }, []);

    // Continuously increment elapsed time when auto-provisioning is active
    React.useEffect(() => {
        if (!latestAutoProvision) {
            setElapsedIncrement(0);
            return;
        }

        const isActive = latestAutoProvision.status === 'in-progress' || latestAutoProvision.status === 'pending';
        if (!isActive) {
            setElapsedIncrement(0);
            return;
        }

        const interval = setInterval(() => {
            setElapsedIncrement(prev => prev + 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [latestAutoProvision?.status]);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.fetchById, [{ param: 'id', value: user?.entityId }]);
        setFetchCompany({ url, method });
    }, [user]);

    React.useEffect(() => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.xero.getIntegration, [{ param: 'entityId', value: user.entityId }]);
        setFetchXeroIntegration({ url, method });
    }, [user?.entityId]);

    React.useEffect(() => {
        const { data, isLoading, error } = staleImport;
        if (data && !isLoading) {
            showSuccess('Stale-data reimport started - check Activity Logs for progress.');
        }
        if (error) {
            showError(error ? error.toString() : 'Failed to start stale-data reimport');
        }
    }, [staleImport]);

    React.useEffect(() => {
        const { data, isLoading, error } = autoReconcile;
        if (data && !isLoading) {
            const { scanned, linked } = data.data || {};
            showSuccess(`Scanned ${scanned ?? 0} unresolved invoice(s), auto-linked ${linked ?? 0}.`);
        }
        if (error) {
            showError(error ? error.toString() : 'Failed to auto-reconcile Xero payments');
        }
    }, [autoReconcile]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCompany;
        if (data && !isLoading) {
            const tolerancePercentValue = data.settings.tolerancePercent || 0;
            const entriesToCompareValue = data.settings.entriesToCompare || 0;
            const descriptionCharacterToCompareValue = data.settings.descriptionCharacterToCompare || 0;
            const rangeSetupValue = data.settings.rangeSetup || rangeSetup;
            const provisionMatchConfigValue = data.settings.provisionMatchConfig || provisionMatchConfig;

            // Set current values
            setTolerancePercent(tolerancePercentValue);
            setEntriesToCompare(entriesToCompareValue);
            setDescriptionCharacterToCompare(descriptionCharacterToCompareValue);
            setRangeSetup(rangeSetupValue);
            setProvisionMatchConfig(provisionMatchConfigValue);

            // Set latest auto provision data
            if (data.misc?.latestAutoProvision) {
                setLatestAutoProvision(data.misc.latestAutoProvision);
            }

            // Store original values for comparison
            setOriginalValues({
                tolerancePercent: tolerancePercentValue,
                entriesToCompare: entriesToCompareValue,
                descriptionCharacterToCompare: descriptionCharacterToCompareValue,
                rangeSetup: rangeSetupValue,
                provisionMatchConfig: provisionMatchConfigValue,
            });
        }
        if (error) {
            showError("Failed to fetch settings.");
        }
    }, [fetchCompany, showError]);

    // Check if any values have changed from original for each section
    const hasProvisionThisChanges = React.useMemo(() => {
        if (!originalValues) return false;
        
        return tolerancePercent !== originalValues.tolerancePercent ||
               entriesToCompare !== originalValues.entriesToCompare ||
               descriptionCharacterToCompare !== originalValues.descriptionCharacterToCompare ||
               JSON.stringify(rangeSetup) !== JSON.stringify(originalValues.rangeSetup);
    }, [originalValues, tolerancePercent, entriesToCompare, descriptionCharacterToCompare, rangeSetup]);

    const hasProvisionReconciliationChanges = React.useMemo(() => {
        if (!originalValues) return false;
        
        return JSON.stringify(provisionMatchConfig) !== JSON.stringify(originalValues.provisionMatchConfig);
    }, [originalValues, provisionMatchConfig]);

    React.useEffect(() => {
        const { data, isLoading, error } = updateCompany;
        if (data && !isLoading) {
            showSuccess("Settings updated successfully.");
            // Update original values after successful save
            setOriginalValues({
                tolerancePercent,
                entriesToCompare,
                descriptionCharacterToCompare,
                rangeSetup,
                provisionMatchConfig,
            });
        }
        if (error) {
            showError("Failed to update settings.");
        }
    }, [updateCompany, showError, showSuccess, tolerancePercent, entriesToCompare, descriptionCharacterToCompare, rangeSetup, provisionMatchConfig]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteCompany;
        if (data && !isLoading) {
            Cookies.remove('token');
            Cookies.remove('entities');
            Cookies.remove('selectedCompany');
            localStorage.removeItem('inCompanySelection');
            window.location.href = '/login';
        }
        if (error) {
            showError("Failed to delete company.");
        }
    }, [deleteCompany]);

    React.useEffect(() => {
        const { data, isLoading, error } = autoProvision;
        if (data && !isLoading) {
            showSuccess("Auto-provisioning initiated successfully.");
        }
        if (error) {
            showError("Failed to auto-provision.");
        }
    }, [autoProvision]);

    const handleUpdateProvisionThis = () => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.update, [{ param: 'id', value: user?.entityId }]);
        
        setUpdateCompany({ url, method, body: {
            tolerancePercent,
            entriesToCompare,
            descriptionCharacterToCompare,
            rangeSetup: JSON.stringify(rangeSetup),
        } });
    };

    const handleUpdateProvisionReconciliation = () => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.update, [{ param: 'id', value: user?.entityId }]);
        
        setUpdateCompany({ url, method, body: {
            provisionMatchConfig: JSON.stringify(provisionMatchConfig),
        } });
    };

    const handleDelete = () => {
        const { url, method } = buildApiRequestConfig(routes.company.hardDelete, [{ param: 'id', value: user?.entityId }]);
        setDeleteCompany({ url, method });
    };

    const handleStaleXeroImport = () => {
        const integration = fetchXeroIntegration.data?.data;
        if (!integration?.bankAccountId) {
            showError("No bank account configured for this entity's Xero integration");
            return;
        }
        const { url, method } = buildApiRequestConfig(routes.xero.syncStale, [{ param: 'entityId', value: user?.entityId }]);
        setStaleImport({ url, method, body: { bankAccountId: integration.bankAccountId } });
    };

    const handleAutoReconcile = () => {
        const { url, method } = buildApiRequestConfig(routes.transaction.autoReconcileXeroPayments, [{ param: 'entityId', value: user?.entityId }]);
        setAutoReconcile({ url, method });
    };

    const handleAutoProvision = () => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.autoProvision, [{ param: 'entityId', value: user?.entityId }]);
        setAutoProvision({ 
            url, 
            method,
            body: {
                ...autoProvisionParams,
                provisionCutoffConfig: provisionCutoffConfig
            }
        });
    };

    const handleCreateBasiqUser = () => {
        const { url, method } = buildApiRequestConfig(routes.basiq.createUser, []);
        console.log('Creating Basiq user:', { url, method, email: basiqEmail });
    };

    const handleOpenConsentUI = () => {
        // connectUser route handles opening the consent UI
        if (basiqUserId) {
            const { url } = buildApiRequestConfig(routes.basiq.connectUser, [{ param: 'basiqUserId', value: basiqUserId }]);
            window.open(url, '_blank');
        } else {
            showError('Please enter a Basiq User ID first');
        }
    };

    const handlePullTransactions = () => {
        const { url, method } = buildApiRequestConfig(routes.basiq.getTransactions);
        setPullTransactions({ url, method, body: { from: basiqTransactionDateRange.from, to: basiqTransactionDateRange.to, userId: basiqUserId } });
    };

    // Helper function to parse and increment elapsed time
    const getIncrementedElapsedTime = (baseTime: string, incrementSeconds: number): string => {
        const parts = baseTime.split(':').map(p => parseInt(p, 10));
        let totalSeconds = 0;

        if (parts.length === 3) {
            totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
        } else if (parts.length === 2) {
            totalSeconds = parts[0] * 60 + parts[1];
        } else {
            totalSeconds = parts[0];
        }

        totalSeconds += incrementSeconds;

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    // Helper function to calculate end date based on start date, number of days, and direction
    const calculateEndDate = (startDate: string, numberOfDays: number, direction: 'asc' | 'desc'): string => {
        const start = new Date(startDate);
        const end = new Date(start);
        
        if (direction === 'asc') {
            // Forward direction: add days
            end.setDate(end.getDate() + numberOfDays);
        } else {
            // Backward direction: subtract days
            end.setDate(end.getDate() - numberOfDays);
        }
        
        // Return date in YYYY-MM-DD format
        return end.toLocaleDateString();
    };

    return (
        <div style={{ width: '100%' }}>
            <DeletePopup
                open={showDeletePopup}
                message="Delete Company"
                description="Are you sure you want to delete this company? This action cannot be undone"
                onConfirm={handleDelete}
                onCancel={() => setShowDeletePopup(false)}
            />
            <SubscriptionPlans
                open={showSubscriptionPlans}
                onClose={() => setShowSubscriptionPlans(false)}
            />
            <Topbar title="Dev Options"/>
            <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <SectionWrapper title="<Provision This> Configuration">
                    <Box display="flex" gap={4} maxWidth={600}>
                        <Box flex={1} display="flex" flexDirection="column" gap={3}>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={0.5}>Provision This Amount Offset</Typography>
                                <TextField 
                                    placeholder="Enter amount" 
                                    fullWidth 
                                    size="small" 
                                    value={tolerancePercent} 
                                    onChange={(e) => setTolerancePercent(Number(e.target.value) || 0)}
                                    type="number"
                                    InputProps={{
                                        endAdornment: '%'
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={2}>Setup of the Ranges</Typography>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    {Object.entries(rangeSetup).map(([key, values]) => (
                                        <Box key={key} display="flex" alignItems="center" gap={2}>
                                            <Typography fontSize={13} sx={{ minWidth: 110, textTransform: 'capitalize' }}>
                                                {key}:
                                            </Typography>
                                            <Typography fontSize={13}>between</Typography>
                                            <TextField 
                                                size="small" 
                                                type="number"
                                                inputProps={{ step: "0.1" }}
                                                value={values[0]}
                                                onChange={(e) => setRangeSetup({
                                                    ...rangeSetup,
                                                    [key]: [Number(e.target.value) || 0, values[1]] as [number, number]
                                                })}
                                                sx={{ width: 80 }}
                                            />
                                            <Typography fontSize={13}>and</Typography>
                                            <TextField 
                                                size="small" 
                                                type="number"
                                                inputProps={{ step: "0.1" }}
                                                value={values[1]}
                                                onChange={(e) => setRangeSetup({
                                                    ...rangeSetup,
                                                    [key]: [values[0], Number(e.target.value) || 0] as [number, number]
                                                })}
                                                sx={{ width: 80 }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={0.5}>Number of Entries to Compare From</Typography>
                                <TextField 
                                    placeholder="Enter number" 
                                    fullWidth 
                                    size="small" 
                                    value={entriesToCompare} 
                                    onChange={(e) => setEntriesToCompare(Number(e.target.value) || 0)}
                                    type="number"
                                />
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={0.5}>Description Comparison Acceptable Score (0-100) <span style={{ color: descriptionCharacterToCompare <= 30 ? 'red' : descriptionCharacterToCompare <= 50 ? 'green' : 'orange' }}>{descriptionCharacterToCompare <= 30 ? 'Lenient' : descriptionCharacterToCompare <= 50 ? 'Moderate' : 'Strict'}</span></Typography>
                                <TextField 
                                    placeholder="Enter number" 
                                    fullWidth 
                                    size="small" 
                                    value={descriptionCharacterToCompare} 
                                    onChange={(e) => setDescriptionCharacterToCompare(Number(e.target.value) || 0)}
                                    type="number"
                                />
                            </Box>
                            <Box>
                                <FormControlLabel
                                    control={<Switch checked={showDebug} onChange={(e) => setShowDebug(e.target.checked)} />}
                                    label={<Typography fontSize={14} fontWeight={500}>Show Debug</Typography>}
                                />
                            </Box>
                            <Box>
                                <FormControlLabel
                                    control={<Switch checked={showCashPositionToggle} onChange={(e) => setShowCashPositionToggle(e.target.checked)} />}
                                    label={<Typography fontSize={14} fontWeight={500}>Show Cash Position Toggle (Transactions page)</Typography>}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            sx={{ color: '#fff' }}
                            onClick={handleUpdateProvisionThis}
                            disabled={!hasProvisionThisChanges}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="<Import: Provision Matching> Configuration">
                    <Box display="flex" gap={4} maxWidth={700}>
                        <Box flex={1} display="flex" flexDirection="column" gap={3}>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={0.5}>Amount Match Tolerance</Typography>
                                <Typography fontSize={12} color="text.secondary" mb={1}>Maximum difference allowed between amounts to be considered a match</Typography>
                                <TextField 
                                    placeholder="Enter tolerance" 
                                    fullWidth 
                                    size="small" 
                                    value={provisionMatchConfig.amountTolerance} 
                                    onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, amountTolerance: Number(e.target.value) || 0})}
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                />
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={0.5}>Amount Match Points</Typography>
                                <Typography fontSize={12} color="text.secondary" mb={1}>Points awarded when imported amount matches provision amount (within tolerance)</Typography>
                                <TextField 
                                    placeholder="Enter points" 
                                    fullWidth 
                                    size="small" 
                                    value={provisionMatchConfig.amountPoints} 
                                    onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, amountPoints: Number(e.target.value) || 0})}
                                    type="number"
                                    inputProps={{ step: "0.05" }}
                                />
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={2}>Date Match Points</Typography>
                                <Typography fontSize={12} color="text.secondary" mb={2}>Points awarded based on date difference between import and provision</Typography>
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography fontSize={13} sx={{ minWidth: 120 }}>Same day:</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            inputProps={{ step: "0.05", min: "0", max: "1" }}
                                            value={provisionMatchConfig.sameDayPoints}
                                            onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, sameDayPoints: Number(e.target.value) || 0})}
                                            sx={{ width: 80 }}
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography fontSize={13} sx={{ minWidth: 120 }}>Within 1 day:</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            inputProps={{ step: "0.05", min: "0", max: "1" }}
                                            value={provisionMatchConfig.within1DayPoints}
                                            onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, within1DayPoints: Number(e.target.value) || 0})}
                                            sx={{ width: 80 }}
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography fontSize={13} sx={{ minWidth: 120 }}>Within 3 days:</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            inputProps={{ step: "0.05", min: "0", max: "1" }}
                                            value={provisionMatchConfig.within3DayPoints}
                                            onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, within3DayPoints: Number(e.target.value) || 0})}
                                            sx={{ width: 80 }}
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography fontSize={13} sx={{ minWidth: 120 }}>Within 7 days:</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            inputProps={{ step: "0.05", min: "0", max: "1" }}
                                            value={provisionMatchConfig.within7DayPoints}
                                            onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, within7DayPoints: Number(e.target.value) || 0})}
                                            sx={{ width: 80 }}
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography fontSize={13} sx={{ minWidth: 120 }}>Within 30 days:</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            inputProps={{ step: "0.05", min: "0", max: "1" }}
                                            value={provisionMatchConfig.within30DayPoints}
                                            onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, within30DayPoints: Number(e.target.value) || 0})}
                                            sx={{ width: 80 }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={2}>Description Token Match Thresholds</Typography>
                                <Typography fontSize={12} color="text.secondary" mb={2}>Define score levels based on matching token count. Tokens are individual words in the description.</Typography>
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    {provisionMatchConfig.descriptionTokenThresholds.map((threshold, index) => (
                                        <Box key={index} display="flex" alignItems="center" gap={2}>
                                            <Typography fontSize={13} sx={{ minWidth: 110 }}>
                                                {threshold.minTokens} token{threshold.minTokens > 1 ? 's' : ''}:
                                            </Typography>
                                            <TextField 
                                                size="small" 
                                                type="number"
                                                label="Score"
                                                inputProps={{ step: "0.05", min: "0" }}
                                                value={threshold.score}
                                                onChange={(e) => {
                                                    const newThresholds = [...provisionMatchConfig.descriptionTokenThresholds];
                                                    newThresholds[index] = { ...threshold, score: Number(e.target.value) || 0 };
                                                    setProvisionMatchConfig({...provisionMatchConfig, descriptionTokenThresholds: newThresholds});
                                                }}
                                                sx={{ width: 100 }}
                                            />
                                            <TextField 
                                                size="small" 
                                                type="number"
                                                label="Min Tokens"
                                                inputProps={{ step: "1", min: "1" }}
                                                value={threshold.minTokens}
                                                onChange={(e) => {
                                                    const newThresholds = [...provisionMatchConfig.descriptionTokenThresholds];
                                                    newThresholds[index] = { ...threshold, minTokens: Number(e.target.value) || 1 };
                                                    setProvisionMatchConfig({...provisionMatchConfig, descriptionTokenThresholds: newThresholds.sort((a, b) => a.minTokens - b.minTokens)});
                                                }}
                                                sx={{ width: 100 }}
                                            />
                                            {provisionMatchConfig.descriptionTokenThresholds.length > 1 && (
                                                <Button 
                                                    size="small" 
                                                    sx={{ color: '#d32f2f' }}
                                                    onClick={() => {
                                                        const newThresholds = provisionMatchConfig.descriptionTokenThresholds.filter((_, i) => i !== index);
                                                        setProvisionMatchConfig({...provisionMatchConfig, descriptionTokenThresholds: newThresholds});
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </Box>
                                    ))}
                                    <Button 
                                        size="small" 
                                        sx={{ color: 'var(--color-primary)', mt: 1 }}
                                        onClick={() => {
                                            const maxTokens = Math.max(...provisionMatchConfig.descriptionTokenThresholds.map(t => t.minTokens));
                                            setProvisionMatchConfig({
                                                ...provisionMatchConfig, 
                                                descriptionTokenThresholds: [
                                                    ...provisionMatchConfig.descriptionTokenThresholds,
                                                    { minTokens: maxTokens + 1, score: 0 }
                                                ]
                                            });
                                        }}
                                    >
                                        + Add Threshold
                                    </Button>
                                </Box>
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={2}>Category Match Points</Typography>
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    <Box>
                                        <Typography fontSize={13} mb={0.5}>Base points (for having a category):</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            inputProps={{ step: "0.05" }}
                                            value={provisionMatchConfig.categoryBasePoints}
                                            onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, categoryBasePoints: Number(e.target.value) || 0})}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box>
                                        <Typography fontSize={13} mb={0.5}>Bonus points (when categories match):</Typography>
                                        <TextField 
                                            size="small" 
                                            type="number"
                                            inputProps={{ step: "0.05" }}
                                            value={provisionMatchConfig.categoryBonusPoints}
                                            onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, categoryBonusPoints: Number(e.target.value) || 0})}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={0.5}>Minimum Match Score Threshold</Typography>
                                <Typography fontSize={12} color="text.secondary" mb={1}>Minimum total score required to consider a provision match (0-5)</Typography>
                                <TextField 
                                    placeholder="Enter threshold" 
                                    fullWidth 
                                    size="small" 
                                    value={provisionMatchConfig.minimumThreshold} 
                                    onChange={(e) => setProvisionMatchConfig({...provisionMatchConfig, minimumThreshold: Number(e.target.value) || 0})}
                                    type="number"
                                    inputProps={{ step: "0.1", min: "0", max: "5" }}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained" 
                            sx={{ color: '#fff' }} 
                            onClick={handleUpdateProvisionReconciliation}
                            disabled={!hasProvisionReconciliationChanges}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Auto-Provisioning">
                    {latestAutoProvision && (
                        <Box sx={{ my: 3, p: 2, backgroundColor: 'var(--color-container-background)', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                                Latest Auto-Provisioning Details
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 2, mb: 3 }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Status</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                                        {latestAutoProvision.status}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Transactions Scanned</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {latestAutoProvision.transactionsScanned}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Provisions Created</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {latestAutoProvision.provisionsCreated}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Elapsed Time</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {getIncrementedElapsedTime(latestAutoProvision.elapsedTime, elapsedIncrement)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Initiated At</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatDate(latestAutoProvision.createdAt, true)}
                                    </Typography>
                                </Box>
                            </Box>
                            {latestAutoProvision.config && latestAutoProvision.config.startDate && (
                                <>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                                        Configuration Used
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Start Date</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatDate(latestAutoProvision.config.startDate, false)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Direction</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                                                {latestAutoProvision.config.direction}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Number of Days</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {latestAutoProvision.config.numberOfDays}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Concurrent Limit</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {latestAutoProvision.config.concurrentLimit}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Time Limit</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {latestAutoProvision.config.timeLimit} minutes
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', mb: 3, gap: 1 }}>
                        <Box flex={0.75}>
                            <Typography fontSize={13} fontWeight={500} mb={0.5}>Start Date</Typography>
                            <TextField 
                                type="date"
                                fullWidth 
                                size="small" 
                                value={autoProvisionParams.startDate} 
                                onChange={(e) => setAutoProvisionParams({...autoProvisionParams, startDate: e.target.value})}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                        <Box flex={0.5}>
                            <Typography fontSize={13} fontWeight={500} mb={0.5}>Number of Days</Typography>
                            <TextField 
                                placeholder="Days" 
                                fullWidth 
                                size="small" 
                                value={autoProvisionParams.numberOfDays} 
                                onChange={(e) => setAutoProvisionParams({...autoProvisionParams, numberOfDays: Number(e.target.value) || 0})}
                                type="number"
                                inputProps={{ min: 1 }}
                            />
                        </Box>
                        <Box flex={0.75}>
                            <Typography fontSize={13} fontWeight={500} mb={0.5}>End Date</Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: 'var(--color-container-background)',
                                borderRadius: 1,
                                color: 'var(--color-default-text)',
                                fontSize: 14,
                                fontWeight: 600,
                                height: 40
                            }}>
                                {calculateEndDate(autoProvisionParams.startDate, autoProvisionParams.numberOfDays, autoProvisionParams.direction)}
                            </Box>
                        </Box>
                        <Box flex={0.5}>
                            <Typography fontSize={13} fontWeight={500} mb={0.5}>Concurrent Limit</Typography>
                            <TextField 
                                placeholder="Limit" 
                                fullWidth 
                                size="small" 
                                value={autoProvisionParams.concurrentLimit} 
                                onChange={(e) => setAutoProvisionParams({...autoProvisionParams, concurrentLimit: Number(e.target.value) || 1})}
                                type="number"
                                inputProps={{ min: 1 }}
                            />
                        </Box>
                        <Box flex={0.5}>
                            <Typography fontSize={13} fontWeight={500} mb={0.5}>Time Limit (min)</Typography>
                            <TextField 
                                placeholder="Minutes" 
                                fullWidth 
                                size="small" 
                                value={autoProvisionParams.timeLimit} 
                                onChange={(e) => setAutoProvisionParams({...autoProvisionParams, timeLimit: Number(e.target.value) || 0})}
                                type="number"
                                inputProps={{ min: 1 }}
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography fontSize={13} fontWeight={500} mb={0.5}>Direction</Typography>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant={autoProvisionParams.direction === 'asc' ? 'contained' : 'outlined'}
                                    size="small"
                                    sx={{ flex: 1, fontSize: 12, color: autoProvisionParams.direction === 'asc' ? '#fff' : undefined, height: 39 }}
                                    onClick={() => setAutoProvisionParams({...autoProvisionParams, direction: 'asc'})}
                                >
                                    Asc
                                </Button>
                                <Button
                                    variant={autoProvisionParams.direction === 'desc' ? 'contained' : 'outlined'}
                                    size="small"
                                    sx={{ flex: 1, fontSize: 12, color: autoProvisionParams.direction === 'desc' ? '#fff' : undefined, height: 39 }}
                                    onClick={() => setAutoProvisionParams({...autoProvisionParams, direction: 'desc'})}
                                >
                                    Desc
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ my: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Provision Occurrence Cutoff Thresholds
                        </Typography>
                        <Typography fontSize={12} color="text.secondary" mb={2}>
                            Define how many days past the expected occurrence a transaction should be ignored (considered stopped recurring). For example, if a weekly transaction is 6 weeks past due, it's likely stopped.
                        </Typography>
                        <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))">
                            {Object.entries(provisionCutoffConfig).map(([key, value]) => (
                                <Box key={key} display="flex" alignItems="center" gap={1}>
                                    <Typography fontSize={13} sx={{ minWidth: 110, textTransform: 'capitalize' }}>
                                        {key}:
                                    </Typography>
                                    <TextField 
                                        size="small" 
                                        type="number"
                                        value={value}
                                        disabled={true}
                                        onChange={(e) => setProvisionCutoffConfig({
                                            ...provisionCutoffConfig,
                                            [key]: Number(e.target.value) || 0
                                        })}
                                        sx={{ width: 120 }}
                                        inputProps={{ min: "0", step: "1" }}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">days</InputAdornment>
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button 
                            variant="contained" 
                            sx={{ color: '#fff' }} 
                            onClick={handleAutoProvision}
                        >
                            Initiate Auto-Provisioning 
                        </Button>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Basiq">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* STEP 1 - BASIQ USER */}
                        <Box sx={{ pb: 3, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography fontSize={13} fontWeight={600} sx={{ mb: 2, color: 'var(--color-text-muted)' }}>
                                STEP 1 – BASIQ USER
                            </Typography>
                            <Typography fontSize={13} sx={{ mb: 2 }}>Create new user by email</Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                                <TextField
                                    placeholder="user@example.com"
                                    size="small"
                                    value={basiqEmail}
                                    onChange={(e) => setBasiqEmail(e.target.value)}
                                    sx={{ flex: 1, maxWidth: 400 }}
                                />
                                <Button
                                    variant="contained"
                                    sx={{ color: '#fff', height: 40 }}
                                    onClick={handleCreateBasiqUser}
                                >
                                    Create User
                                </Button>
                            </Box>
                            <Typography fontSize={12} sx={{ mb: 2, color: 'var(--color-text-muted)' }}>– or paste an existing Basiq User ID –</Typography>
                            <TextField
                                placeholder="e.g. ea3a81 or full UUID"
                                size="small"
                                fullWidth
                                value={basiqUserId}
                                onChange={(e) => setBasiqUserId(e.target.value)}
                                sx={{ maxWidth: 500 }}
                            />
                        </Box>

                        {/* STEP 2 - CONNECT BANK */}
                        <Box sx={{ pb: 3, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography fontSize={13} fontWeight={600} sx={{ mb: 2, color: 'var(--color-text-muted)' }}>
                                STEP 2 – CONNECT BANK (CONSENT UI)
                            </Typography>
                            <Typography fontSize={13} sx={{ mb: 3 }}>
                                Opens Basiq Consent UI in a new tab. For sandbox use <span style={{ fontWeight: 600 }}>Hooli Bank</span> → Wentworth-Smith / whistler
                            </Typography>
                            <Button
                                variant="outlined"
                                sx={{ color: 'var(--color-default-text)', borderColor: 'var(--color-text-muted)' }}
                                onClick={handleOpenConsentUI}
                            >
                                Open Consent UI ↗
                            </Button>
                        </Box>

                        {/* STEP 3 - PULL TRANSACTIONS */}
                        <Box>
                            <Typography fontSize={13} fontWeight={600} sx={{ mb: 2, color: 'var(--color-text-muted)' }}>
                                STEP 3 – PULL TRANSACTIONS
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography fontSize={13} fontWeight={500} mb={0.5}>From</Typography>
                                    <TextField
                                        type="date"
                                        size="small"
                                        value={basiqTransactionDateRange.from}
                                        onChange={(e) => setBasiqTransactionDateRange({ ...basiqTransactionDateRange, from: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 180 }}
                                    />
                                </Box>
                                <Box>
                                    <Typography fontSize={13} fontWeight={500} mb={0.5}>To</Typography>
                                    <TextField
                                        type="date"
                                        size="small"
                                        value={basiqTransactionDateRange.to}
                                        onChange={(e) => setBasiqTransactionDateRange({ ...basiqTransactionDateRange, to: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 180 }}
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    sx={{ color: '#fff' }}
                                    onClick={handlePullTransactions}
                                >
                                    Pull Transactions
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Xero - Reimport Stale Data">
                    <Button
                        variant="contained"
                        sx={{ color: '#fff' }}
                        onClick={handleStaleXeroImport}
                        disabled={!fetchXeroIntegration.data?.data?.bankAccountId || staleImport.isLoading}
                    >
                        {staleImport.isLoading ? 'Starting…' : 'Reimport Stale Xero Data'}
                    </Button>
                </SectionWrapper>
                <SectionWrapper title="Xero - Auto Reconcile Invoices">
                    <Typography fontSize={13} color="text.secondary" mb={2}>
                        Scans every unresolved invoice's recorded Xero payment(s) and auto-links it when exactly one unlinked transaction matches by both date and amount, or when a transaction's amount is accounted for by a confident group of same-day invoices to the same client. Anything ambiguous is left alone for manual review in the invoice's own Xero Payment tab.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ color: '#fff' }}
                        onClick={handleAutoReconcile}
                        disabled={autoReconcile.isLoading}
                    >
                        {autoReconcile.isLoading ? 'Reconciling…' : 'Auto Reconcile Xero Invoices'}
                    </Button>
                    {autoReconcile.data?.data && (
                        <Typography fontSize={13} sx={{ mt: 2 }}>
                            Scanned {autoReconcile.data.data.scanned} unresolved invoice(s), auto-linked {autoReconcile.data.data.linked}.
                        </Typography>
                    )}
                </SectionWrapper>
                <SectionWrapper title="Subscription Plans (Preview)">
                    <Typography fontSize={13} color="text.secondary" mb={2}>
                        Preview of the subscription plan selection UI built from the package matrix - not wired up to real billing yet.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ color: '#fff' }}
                        onClick={() => setShowSubscriptionPlans(true)}
                    >
                        Preview Subscription Plans
                    </Button>
                </SectionWrapper>
                <SectionWrapper title="Danger Zone">
                    <Button 
                        variant="contained" 
                        sx={{ color: '#fff', backgroundColor: '#d32f2f', mr: 2 }} 
                        onClick={() => setShowDeletePopup(true)}
                    >
                        Delete Company
                    </Button>
                </SectionWrapper>
            </div>
        </div>
    );
};

export default DevOptions;
